/**
 * Tiny debounce hook. Returns a memoized function that delays invoking
 * `callback` until `delayMs` has elapsed since the last call. The trailing
 * call always uses the most recent arguments.
 *
 * Why a hand-rolled hook instead of lodash.debounce: we want React-aware
 * cleanup on unmount, and the existing repo doesn't depend on lodash. Ten
 * lines beats a 70 kB import.
 *
 * Used by MonitorDetailPage to collapse rapid IR-form and threshold-slider
 * onChange events into a single PATCH after the user stops dragging/typing.
 */
import { useEffect, useMemo, useRef } from "react";

export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delayMs: number,
): (...args: TArgs) => void {
  // Hold the latest callback in a ref so the debounced wrapper closes over
  // a stable identity but always invokes the current implementation.
  const cbRef = useRef(callback);
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debounced = useMemo(
    () =>
      (...args: TArgs) => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          cbRef.current(...args);
        }, delayMs);
      },
    [delayMs],
  );

  // Cancel any pending invocation on unmount so we don't fire after the
  // component is gone.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return debounced;
}
